import React, { useState, useEffect, useRef, forwardRef, Ref, useImperativeHandle, ForwardedRef } from 'react';
import { FilterItemDto } from 'models/dtoTypes';
import { IFilterField } from 'models/types';
import { styled } from '@mui/material/styles';
import { get } from 'lodash';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Link from 'components/Link';
import FilterPanel from 'components/FilterPanel';

const PREFIX = 'ResourceTable';
const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    color: 'black',
    fontWeight: 'bold',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
    // borderRadius: 5,
    borderTop: '1px solid',
    borderBottom: '1px solid',
    '&:first-of-type': {
      borderLeft: '1px solid',
      borderTopLeftRadius: 5,
      borderBottomLeftRadius: 5
    },
    '&:last-child': {
      borderRight: '1px solid',
      borderTopRightRadius: 5,
      borderBottomRightRadius: 5,
      // transform: 'scale(1.05)',
    }
  },
}));

const classes = {
  root: `${PREFIX}-root`,
  actions: `${PREFIX}-actions`,
  pagination: `${PREFIX}-pagination`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  [`& .${classes.pagination}`]: {
    paddingTop: 10,
    paddingBottom: 10,
  }
}));

export type IActionFn<TAttributes> = (model: TAttributes, shouldCancel?: () => boolean) => void | Promise<TAttributes | undefined | void>;

export interface ITableRowAction<TAttributes> {
  label: string;
  onClick: IActionFn<TAttributes>;
  startOnDefault?: boolean;
  hide?: (model: TAttributes) => boolean;
}

export interface IColumn<TAttributes> {
  label: string;
  dataIndex?: string;
  sortIndex?: string;
  actions?: ITableRowAction<TAttributes>[];
  render?: (model: TAttributes) => JSX.Element | string;
}

type ResourceWithCount<TAttributes> = {
  pageCount?: number;
  count?: number;
  page?: number;
  items?: TAttributes[] | null;
};


interface IProps<TAttributes> {
  stickyHeader?: boolean;
  filterSchema?: IFilterField[];
  columns: IColumn<TAttributes>[];
  onFetch: (options: { pageIndex: number; organisationId?: string;}) => Promise<ResourceWithCount<TAttributes>>;
  defaultQuery?: FilterItemDto[];
  defaultSort?: { sortColumn: string, isDesc: boolean };
  isRowSelectable?: (row: TAttributes) => boolean;
  onSelectRow?: (row: TAttributes) => void;
}

interface IFilters {
  [key: string]: string;
}

export function SingleItem<TAttributes>(props: {
  row: TAttributes,
  columns: IColumn<TAttributes>[],
  hover?: boolean;
  onClick?: (row: TAttributes) => void;
}) {
  const isMounted = useRef(false);
  const { row, columns, hover, onClick } = props;
  const [model, setModel] = useState<TAttributes>(row);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const renderCell = (column: IColumn<TAttributes>) => {
    const { dataIndex, actions, render } = column;

    if (actions) {
      const processAction = async (fn: IActionFn<TAttributes>) => {
        setLoading(true);
        const checkMountedFn = () => !isMounted.current;
        const newModel = await fn(model, checkMountedFn);
        if (newModel) {
          setModel(newModel);
        }
        setLoading(false);
      };

      // render action list
      if (loading) {
        return <CircularProgress size={20}/>;
      }
      return (
        <Stack direction="row" spacing={1}>
          {actions.filter(a => !(a.hide && a.hide(model))).map(action => {
            const { label, onClick, startOnDefault } = action;
            if (startOnDefault) {
              onClick && processAction(onClick);
            }
            return (
              <Link key={label} isAsync
                onClick={() => onClick && processAction(onClick)}
                text={label}
              />
            );
          })}
        </Stack>
      );
    } else if (render) {
      // render customised cell
      return render(model);
    } else if(dataIndex) {
      return get(model, dataIndex || '');
    } else {
      return '';
    }
  };

  return (
    <TableRow hover={hover} onClick={() => onClick && onClick(row)}>
      {columns.map((c, index) => <TableCell key={get(row, 'id') || index}>{renderCell(c)}</TableCell>) }
    </TableRow>
  );
}

export interface IResourceTableRef {
  loadResources(pageIndex: number, reset?: boolean): void;
}


function ResourceTable<TAttributes>(props: IProps<TAttributes>, ref: Ref<IResourceTableRef>) {
  const {
    columns,
    onFetch,
    defaultQuery = [],
    filterSchema,
    defaultSort,
    isRowSelectable,
    onSelectRow,
    stickyHeader = false,
  } = props;
  const [filters, setFilters] = useState<IFilters>({});
  const [filtered, setFiltered] = useState(false);
  const [sort, setSort] = useState<{ sortColumn: string, isDesc: boolean} | undefined>(defaultSort);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResourceWithCount<TAttributes>>();

  const loadResources = async (pageIndex: number, reset = false) => {
    setLoading(true);
    const filterItems = reset ? [] : Object.keys(filters).map(k => ({ column: k, value: filters[k] }));
    const options = {
      filters: [
        ...defaultQuery,
        ...filterItems
      ],
      pageIndex,
      ...(sort || {}),
    };
    try {
      const data = await onFetch(options);
      setResult(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useImperativeHandle(
    ref,
    () => ({
      loadResources,
    }),
    [loadResources],
  );

  useEffect(() => {
    void loadResources(page - 1);
  }, [page, sort]);

  const rows = result?.items || [];

  // Avoid a layout jump when reaching the last page with empty rows.
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeFilter = (value: string, name: string) => {
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const handleFilter = async () => {
    setLoading(true);

    const hasFilters = Object.values(filters).find(v => v !== undefined && v !== null);
    if (hasFilters) {
      await loadResources(0);
      setFiltered(true);
    }
    setLoading(false);
  };

  const handleReset = async () => {
    setLoading(true);
    if (filtered) {
      setFilters({});
      setFiltered(false);
      await loadResources(0, true);
    }
    setLoading(false);
  };

  const handleSort = (sortColumn: string) => {
    const previousSortIndex = sort?.sortColumn;
    if (previousSortIndex === sortColumn) {
      setSort({
        sortColumn,
        isDesc: !sort?.isDesc,
      });
    } else {
      setSort({
        sortColumn,
        isDesc: false,
      });
    }
  };

  const renderLoadingRows = () => {
    return (
      <>
        <LinearProgress />
      </>
    );
  };

  const renderTableHead = () => {
    const { sortColumn, isDesc } = sort || {};
    return (
      <TableHead>
        <TableRow>
          {columns.map((c, index) => {
            const { label, sortIndex } = c;
            if (sortIndex) {
              return (
                <StyledTableCell key={index}>
                  <TableSortLabel
                    active={sortColumn === sortIndex}
                    direction={isDesc ? 'desc' : 'asc'}
                    onClick={() => handleSort(sortIndex)}
                  >
                    {label}
                  </TableSortLabel>
                </StyledTableCell>
              );
            }
            return (
              <StyledTableCell key={index}>
                {label}
              </StyledTableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  };

  return (
    <Root>
      {filterSchema && (
        <FilterPanel
          fields={filterSchema}
          filtered={filtered}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          onSearch={handleFilter}
          onReset={handleReset}
        />
      )}
      <TableContainer component={Paper} sx={{ maxHeight: 1000 }}>
        {loading && renderLoadingRows()}

        {!loading && (
          <Table stickyHeader={stickyHeader} sx={{ minWidth: 500 }} aria-label="custom pagination table">
            {renderTableHead()}
            <TableBody>
              {rows.map((row, index) => {
                row['userRole'] = row['userRole'] === 'SME' ? 'Business' : row['userRole'];
                const isSelectable = Boolean(isRowSelectable && isRowSelectable(row));
                return (
                  <SingleItem
                    key={index}
                    row={row}
                    columns={columns}
                    hover={isSelectable}
                    onClick={onSelectRow}
                  />
                );
              })}
            </TableBody>
          </Table>
        )}
        {result && !loading && (
          <div className={classes.pagination}>
            <Pagination
              count={result.pageCount}
              page={page}
              onChange={handleChangePage}
            />
          </div>
        )}
      </TableContainer>
    </Root>
  );
}

export default forwardRef(ResourceTable) as <T>(
  props: IProps<T> & { ref?: ForwardedRef<IResourceTableRef> },
) => ReturnType<typeof ResourceTable>;

